import { useAuth } from "@/lib/contexts/auth-context";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Building2, MapPin, BarChart3, Users, TrendingUp, CheckCircle2, Map } from "lucide-react";
import { useEffect, useState } from "react";
import { apiClient } from "@/lib/api/client";
import { DashboardStatsDto } from "@/lib/types/api";

export default function DashboardHome() {
  const { user } = useAuth();
  const [stats, setStats] = useState<DashboardStatsDto | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadDashboardStats();
  }, []);

  const loadDashboardStats = async () => {
    try {
      setIsLoading(true);
      const response = await apiClient.getDashboardStats();
      if (response.success && response.data) {
        setStats(response.data);
      }
    } catch (error) {
      // Silently fail - dashboard will show without stats
      console.error("Dashboard stats error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const isSuperAdmin = user?.roles?.includes("SuperAdmin");
  const isAdmin = user?.roles?.includes("Admin");
  const isUser = user?.roles?.includes("User");

  // Define stats cards based on role
  const getStatCards = () => {
    if (!stats) return [];

    const cards = [];

    if (isSuperAdmin) {
      cards.push(
        {
          name: "Departments",
          icon: Building2,
          value: stats.departmentsCount.toString(),
          description: "Total departments",
          color: "bg-blue-500",
        },
        {
          name: "States",
          icon: MapPin,
          value: stats.statesCount?.toString() || "0",
          description: "Total states",
          color: "bg-green-500",
        },
        {
          name: "Districts",
          icon: Map,
          value: stats.districtsCount?.toString() || "0",
          description: "Total districts",
          color: "bg-purple-500",
        },
        {
          name: "Blocks",
          icon: Map,
          value: stats.blocksCount?.toString() || "0",
          description: "Total blocks",
          color: "bg-yellow-500",
        },
        {
          name: "Indicators",
          icon: BarChart3,
          value: stats.indicatorsCount.toString(),
          description: "Performance indicators",
          color: "bg-indigo-500",
        },
        {
          name: "Users",
          icon: Users,
          value: stats.usersCount?.toString() || "0",
          description: "Active users",
          color: "bg-orange-500",
        }
      );
    } else if (isAdmin) {
      cards.push(
        {
          name: "Departments",
          icon: Building2,
          value: stats.departmentsCount.toString(),
          description: "Total departments",
          color: "bg-blue-500",
        },
        {
          name: "Indicators",
          icon: BarChart3,
          value: stats.indicatorsCount.toString(),
          description: "Performance indicators",
          color: "bg-purple-500",
        }
      );
    } else if (isUser) {
      const totalIndicators = stats.totalIndicators || 0;
      const filledCount = stats.filledDataCount || 0;
      const totalPeriods = stats.totalPeriods || 0;
      const pendingPeriods = totalPeriods - filledCount;
      
      cards.push(
        {
          name: "Total Indicators",
          icon: TrendingUp,
          value: totalIndicators.toString(),
          description: user?.blockId ? "Block level indicators" : "District level indicators",
          color: "bg-blue-500",
        },
        {
          name: "Data Progress",
          icon: CheckCircle2,
          value: `${filledCount} / ${totalPeriods}`,
          description: `${pendingPeriods} periods remaining`,
          color: "bg-green-500",
        }
      );
    }

    return cards;
  };

  const statCards = getStatCards();

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 colorful:text-gray-900 yellow:text-[#78350f] pink:text-[#831843] orange:text-[#7c2d12] blue:text-[#1e3a8a] red:text-[#7f1d1d] green:text-[#14532d]">
          Welcome back, {user?.firstName}!
        </h1>
        <p className="mt-2 text-gray-600 dark:text-gray-400 colorful:text-gray-700 yellow:text-[#78350f]/80 pink:text-[#831843]/80 orange:text-[#7c2d12]/80 blue:text-[#1e3a8a]/80 red:text-[#7f1d1d]/80 green:text-[#14532d]/80">
          Here's an overview of your aspirational dashboard.
        </p>
      </div>

      {isLoading ? (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i}>
              <CardHeader className="pb-2">
                <div className="h-4 w-24 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
              </CardHeader>
              <CardContent>
                <div className="h-8 w-16 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className={`grid gap-6 md:grid-cols-2 ${statCards.length > 2 ? 'lg:grid-cols-3 xl:grid-cols-4' : 'lg:grid-cols-2'}`}>
          {statCards.map((stat) => (
            <Card key={stat.name}>
              <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
                <CardTitle className="text-sm font-medium text-gray-600 dark:text-gray-400 colorful:text-gray-700 yellow:text-[#78350f]/80 pink:text-[#831843]/80 orange:text-[#7c2d12]/80 blue:text-[#1e3a8a]/80 red:text-[#7f1d1d]/80 green:text-[#14532d]/80">
                  {stat.name}
                </CardTitle>
                <div className={`p-2 rounded-lg ${stat.color} bg-opacity-10`}>
                  <stat.icon className={`h-5 w-5 ${stat.color.replace('bg-', 'text-')}`} />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-gray-900 dark:text-gray-100 colorful:text-gray-900 yellow:text-[#78350f] pink:text-[#831843] orange:text-[#7c2d12] blue:text-[#1e3a8a] red:text-[#7f1d1d] green:text-[#14532d]">
                  {stat.value}
                </div>
                <p className="text-xs text-gray-600 dark:text-gray-400 colorful:text-gray-700 yellow:text-[#78350f]/70 pink:text-[#831843]/70 orange:text-[#7c2d12]/70 blue:text-[#1e3a8a]/70 red:text-[#7f1d1d]/70 green:text-[#14532d]/70 mt-1">
                  {stat.description}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
